import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { Figure, InlineBar, RankedBars, Columns, Ledger } from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import { fmtInt, fmtMonth, fmtNum, inPeriod, periodName, pluralWord } from "~/lib/format";
import { ed, btn, tbl, Masthead } from "~/components/editorial";

interface CapitalizationCategory {
  category: string;
  label: string;
  isCapitalizable: boolean;
  prCount: number;
  issueCount: number;
  storyPoints: number;
  estimatedHours: number;
  percentage: number;
}

interface DeveloperCapitalization {
  developerId: string;
  name: string;
  avatarUrl: string | null;
  totalHours: number;
  capitalizableHours: number;
  capitalizablePercentage: number;
  capacityHours: number;
  cappedByCapacity: boolean;
  breakdown: {
    category: string;
    hours: number;
    percentage: number;
  }[];
}

interface MonthlyTrend {
  month: string;
  capitalizableHours: number;
  nonCapitalizableHours: number;
  capitalizablePercentage: number;
}

interface CostCapitalizationData {
  period: {
    from: string;
    to: string;
    daysInPeriod: number;
  };
  summary: {
    totalEstimatedHours: number;
    capitalizableHours: number;
    nonCapitalizableHours: number;
    capitalizablePercentage: number;
    totalPRs: number;
    linkedPRs: number;
    linkageRate: number;
  };
  categories: CapitalizationCategory[];
  byDeveloper: DeveloperCapitalization[];
  monthlyTrend: MonthlyTrend[];
  adjustments: {
    hoursPerDay: number;
    maxHoursPerPR: number;
    maxHoursPerIssue: number;
    sizeLinesCap: number;
    prsCappedByPRLimit: number;
    prsDampenedAsGenerated: number;
    developersCappedByCapacity: number;
    modeledHoursBeforeCaps: number;
    hoursRemovedByCaps: number;
  };
  taxCredit: {
    likelyHours: number;
    possibleHours: number;
    excludedHours: number;
    scenarios: {
      id: string;
      label: string;
      description: string;
      eligibleHours: number;
    }[];
    creditRateRange: { low: number; high: number };
    payrollOffsetCap: number;
    notes: string[];
    disclaimer: string;
  };
  methodology: {
    hoursPerStoryPoint: number;
    hoursPerPR: number;
    hoursPer100Lines: number;
    hoursPerExtraCommit: number;
    hoursPerDay: number;
    maxHoursPerPR: number;
    maxHoursPerIssue: number;
    sizeLinesCap: number;
    note: string;
    standardsNote: string;
    classificationRules: {
      category: string;
      label: string;
      source: "jira" | "pr" | "fallback";
      isCapitalizable: boolean;
      rationale: string;
      qreEligibility: "likely" | "possible" | "excluded";
      qreRationale: string;
    }[];
    caveats: string[];
  };
}

function formatMoney(value: number): string {
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

function hours(v: number): string {
  return `${fmtInt(v)}h`;
}

export function CostCapitalizationPage() {
  const { range, window: periodWin } = usePeriod();
  const now = periodName(periodWin.label);

  // Blended fully-loaded hourly cost used for tax credit scenario math.
  // User-adjustable; the server only models hours.
  const [hourlyRate, setHourlyRate] = useState(100);

  const { data, isLoading, error } = useQuery({
    queryKey: ["cost-capitalization", range],
    queryFn: () =>
      api.get<{ data: CostCapitalizationData }>(
        `/metrics/cost-capitalization?range=${range}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const costData = data?.data;

  const handleExport = () => {
    window.open(
      `/api/metrics/cost-capitalization/export?range=${range}`,
      "_blank",
    );
  };

  const devRows = costData
    ? [...costData.byDeveloper].sort((a, b) => b.capitalizableHours - a.capitalizableHours)
    : [];
  const shownDevs = devRows.slice(0, FIGURE_ROWS);
  const cats = costData ? [...costData.categories].sort((a, b) => b.estimatedHours - a.estimatedHours) : [];
  const capCats = cats.filter((c) => c.isCapitalizable);
  const adj = costData?.adjustments;
  const anyBounds =
    !!adj &&
    (adj.developersCappedByCapacity > 0 ||
      adj.prsDampenedAsGenerated > 0 ||
      adj.prsCappedByPRLimit > 0);
  const rate = costData?.taxCredit.creditRateRange;

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead
          links={[{ to: "/reports", label: "Reports" }, { to: "/cost-capitalization", label: "Cost capitalization" }]}
          meta={periodWin.label}
        />

        {isLoading ? (
          <div className={ed.loading}>Loading cost capitalization…</div>
        ) : error || !costData ? (
          <div className={ed.loading}>No data available.</div>
        ) : (
          <>
            <div className={ed.kicker}>Finance · {now}</div>
            <h1 className={ed.headline}>
              {costData.summary.totalEstimatedHours === 0
                ? `No merged work to classify ${inPeriod(periodWin.label)}`
                : `${costData.summary.capitalizablePercentage}% of modeled engineering hours were capitalizable ${inPeriod(periodWin.label)}`}
            </h1>
            <p className={ed.dek}>
              {hours(costData.summary.capitalizableHours)} of{" "}
              {hours(costData.summary.totalEstimatedHours)} went to{" "}
              {capCats.length > 0
                ? capCats.map((c) => c.label.toLowerCase()).join(" and ")
                : "capitalizable work"}
              . {costData.summary.linkageRate}% of merged PRs link to a JIRA issue, which drives the
              classification; the rest fall back to branch and title patterns or are expensed.
            </p>
            <div className={ed.bylineSplit}>
              <span>
                <b>R&amp;D cost capitalization</b> · modeled hours from{" "}
                <span className="num">{fmtNum(costData.summary.totalPRs)}</span> merged PRs and
                their JIRA issues, {costData.period.daysInPeriod} days. Hours are modeled, not
                timesheet hours.
              </span>
              <button type="button" onClick={handleExport} className={btn.primary}>
                Export CSV
              </button>
            </div>

            <Ledger
              hideMissingPrior
              items={[
                {
                  label: "Capitalizable",
                  value: `${costData.summary.capitalizablePercentage}%`,
                  note: "of modeled hours",
                },
                { label: "R&D hours", value: hours(costData.summary.capitalizableHours), note: "capitalizable" },
                { label: "Expensed", value: hours(costData.summary.nonCapitalizableHours) },
                { label: "Total hours", value: hours(costData.summary.totalEstimatedHours), note: "modeled" },
                {
                  label: "JIRA linked",
                  value: `${costData.summary.linkageRate}%`,
                  note: `${fmtNum(costData.summary.linkedPRs)} of ${fmtNum(costData.summary.totalPRs)} PRs`,
                  valueFlag: costData.summary.linkageRate < 50 ? "caution" : undefined,
                },
                {
                  label: "Removed by bounds",
                  value: hours(costData.adjustments.hoursRemovedByCaps),
                  note: `of ${hours(costData.adjustments.modeledHoursBeforeCaps)} modeled`,
                },
              ]}
            />

            <div className={ed.sect}>I. The split</div>
            <h2 className={ed.h2}>
              {cats[0]
                ? `${cats[0].label} took the most hours, ${hours(cats[0].estimatedHours)} (${cats[0].percentage}%)`
                : "No categorized work"}
            </h2>
            {cats.length > 0 && (
              <Figure
                title="Modeled hours by work category"
                sub="Capitalizable categories in color, expensed in gray."
                method="Category from the linked JIRA issue type, else the PR's branch and title, else unlinked (always expensed). Hours after the bounds in the methodology."
              >
                <RankedBars
                  ariaLabel="Modeled hours by work category"
                  format={(v) => `${fmtInt(v)}h`}
                  rows={cats.map((c) => ({
                    id: c.category,
                    label: c.label,
                    value: c.estimatedHours,
                    emphasis: c.isCapitalizable,
                  }))}
                />
              </Figure>
            )}
            <div className={tbl.scroll} style={{ marginTop: 16 }}>
              <table className={tbl.table}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Treatment</th>
                    <th data-num>PRs</th>
                    <th data-num>Story points</th>
                    <th data-num>Est. hours</th>
                    <th data-num>Share</th>
                    <th aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {cats.map((cat) => (
                    <tr key={cat.category}>
                      <td style={{ fontWeight: 600 }}>{cat.label}</td>
                      <td>
                        {cat.isCapitalizable ? (
                          <span className={tbl.label}>Capitalize</span>
                        ) : (
                          <span className={tbl.tag}>Expense</span>
                        )}
                      </td>
                      <td data-num>{fmtNum(cat.prCount)}</td>
                      <td data-num>
                        <span className={cat.storyPoints > 0 ? undefined : ed.muted}>
                          {cat.storyPoints > 0 ? fmtNum(cat.storyPoints) : "–"}
                        </span>
                      </td>
                      <td data-num>{hours(cat.estimatedHours)}</td>
                      <td data-num>{cat.percentage}%</td>
                      <td>
                        <InlineBar value={cat.percentage} max={100} width={60} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {costData.monthlyTrend.length > 0 && (
              <>
                <div className={ed.sect}>II. By month</div>
                <h2 className={ed.h2}>
                  {costData.monthlyTrend.length === 1
                    ? `${fmtMonth(costData.monthlyTrend[0]!.month)}: ${costData.monthlyTrend[0]!.capitalizablePercentage}% capitalizable`
                    : `Capitalizable share ranged from ${Math.min(...costData.monthlyTrend.map((m) => m.capitalizablePercentage))}% to ${Math.max(...costData.monthlyTrend.map((m) => m.capitalizablePercentage))}% by month`}
                </h2>
                <Figure
                  title="Modeled hours per month"
                  sub="Capitalizable in color, expensed in gray; capitalizable share above each column."
                  method="PRs bucketed by merge month inside the period. Same hour model and bounds as the totals."
                  table={{
                    columns: ["Month", "Capitalizable", "Expensed", "Share"],
                    rows: costData.monthlyTrend.map((m) => [
                      fmtMonth(m.month),
                      hours(m.capitalizableHours),
                      hours(m.nonCapitalizableHours),
                      `${m.capitalizablePercentage}%`,
                    ]),
                  }}
                >
                  <Columns
                    ariaLabel="Capitalizable and expensed hours per month"
                    valueLabel="Capitalizable"
                    stackLabel="Expensed"
                    tone="emph"
                    formatKey={fmtMonth}
                    format={(v) => `${fmtInt(v)}h`}
                    tickFormat={(v) => `${fmtInt(v)}h`}
                    height={170}
                    margin={{ t: 24, l: 52 }}
                    steps={2}
                    barRatio={0.68}
                    maxBarWidth={48}
                    points={costData.monthlyTrend.map((m) => ({
                      key: m.month,
                      value: m.capitalizableHours,
                      stack: m.nonCapitalizableHours,
                      note: `${Math.round(m.capitalizablePercentage)}%`,
                    }))}
                  />
                </Figure>
              </>
            )}

            <div className={ed.sect}>III. By developer</div>
            <h2 className={ed.h2}>
              Capitalizable hours for {fmtNum(devRows.length)}{" "}
              {pluralWord(devRows.length, "developer")}
            </h2>
            {shownDevs.length > 0 && (
              <Figure
                title="Capitalizable hours per developer"
                sub="Gray bars; the table below has each person's share and top categories."
                method={`Modeled hours on PRs each developer authored, capped at ${costData.methodology.hoursPerDay} hours per active business day.${devRows.length > shownDevs.length ? ` Showing ${shownDevs.length} of ${devRows.length}.` : ""}`}
              >
                <RankedBars
                  ariaLabel="Capitalizable hours per developer"
                  format={(v) => `${fmtInt(v)}h`}
                  rows={shownDevs.map((d) => ({
                    id: d.developerId,
                    label: d.name,
                    value: d.capitalizableHours,
                  }))}
                />
              </Figure>
            )}
            <div className={tbl.scroll} style={{ marginTop: 16 }}>
              <table className={tbl.table}>
                <thead>
                  <tr>
                    <th>Developer</th>
                    <th data-num>Total hours</th>
                    <th data-num>Capitalizable</th>
                    <th data-num>Share</th>
                    <th aria-hidden />
                    <th>Top categories</th>
                  </tr>
                </thead>
                <tbody>
                  {devRows.map((dev) => (
                    <tr key={dev.developerId}>
                      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{dev.name}</td>
                      <td data-num>
                        {hours(dev.totalHours)}
                        {dev.cappedByCapacity && (
                          <span
                            className={tbl.aside}
                            title={`Clamped to full-time capacity (${fmtInt(dev.capacityHours)}h) for the window this developer was active. Raw modeled hours were higher, usually from large or generated PRs.`}
                          >
                            capped
                          </span>
                        )}
                      </td>
                      <td data-num>{hours(dev.capitalizableHours)}</td>
                      <td data-num>{Math.round(dev.capitalizablePercentage)}%</td>
                      <td>
                        <InlineBar value={dev.capitalizablePercentage} max={100} width={56} />
                      </td>
                      <td style={{ fontSize: 11.5, color: "var(--colors-ed-ink2)" }}>
                        {dev.breakdown.slice(0, 3).map((b, i) => (
                          <span key={b.category}>
                            {i > 0 ? ", " : ""}
                            {b.category} <span className={ed.num}>{Math.round(b.percentage)}%</span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={ed.sect}>IV. R&amp;D tax credit (IRC Section 41)</div>
            <h2 className={ed.h2}>
              {hours(costData.taxCredit.likelyHours)} likely qualify as research hours
            </h2>
            <div className={styles.taxIntro}>
              <p className={ed.small}>
                A separate lens from book capitalization: the federal R&amp;D tax credit applies to
                qualified research expenses (QREs), mostly wages for work that meets the IRS
                four-part test. Hours below are bucketed by how likely each work category is to
                qualify.
              </p>
              <label className={styles.rateInput}>
                <span>Blended hourly cost</span>
                <span className={styles.rateField}>
                  <span>$</span>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={hourlyRate}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v > 0) setHourlyRate(v);
                    }}
                  />
                  <span>/hr</span>
                </span>
              </label>
            </div>
            <Figure
              title="Hours by QRE likelihood"
              sub="Likely in color."
              method="Each work category maps to likely, possible or excluded; see the classification table below."
            >
              <RankedBars
                ariaLabel="Hours by QRE likelihood"
                format={(v) => `${fmtInt(v)}h`}
                rows={[
                  { id: "likely", label: "Likely QRE", value: costData.taxCredit.likelyHours, emphasis: true },
                  { id: "possible", label: "Possible", value: costData.taxCredit.possibleHours },
                  { id: "excluded", label: "Excluded", value: costData.taxCredit.excludedHours },
                ]}
              />
            </Figure>

            <div className={tbl.scroll} style={{ marginTop: 16 }}>
              <table className={tbl.table}>
                <thead>
                  <tr>
                    <th>Scenario</th>
                    <th data-num>Eligible hours</th>
                    <th data-num>Est. wage base</th>
                    <th data-num>
                      Est. federal credit ({rate ? `${Math.round(rate.low * 1000) / 10}` : ""} to{" "}
                      {rate ? `${Math.round(rate.high * 1000) / 10}` : ""}%)
                    </th>
                    <th>Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {costData.taxCredit.scenarios.map((scenario) => {
                    const wageBase = scenario.eligibleHours * hourlyRate;
                    return (
                      <tr key={scenario.id}>
                        <td style={{ fontWeight: 600 }}>{scenario.label}</td>
                        <td data-num>{hours(scenario.eligibleHours)}</td>
                        <td data-num>{formatMoney(wageBase)}</td>
                        <td data-num>
                          {formatMoney(wageBase * costData.taxCredit.creditRateRange.low)} to{" "}
                          {formatMoney(wageBase * costData.taxCredit.creditRateRange.high)}
                        </td>
                        <td>{scenario.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ul className={ed.bullets} style={{ marginTop: 12 }}>
              {costData.taxCredit.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
              <li>
                Qualified small businesses (under $5M gross receipts, fewer than 5 years of
                receipts) may apply up to {formatMoney(costData.taxCredit.payrollOffsetCap)} of the
                credit per year against payroll tax instead of income tax.
              </li>
            </ul>
            <p className={ed.methodMono}>{costData.taxCredit.disclaimer}</p>

            <div className={ed.sect}>V. Methodology</div>
            <h3 className={ed.h3Sm}>How hours are estimated</h3>
            <p className={ed.small}>
              Every merged PR in the period gets a modeled effort estimate:{" "}
              <span className="num">{costData.methodology.hoursPerPR}</span> base hours, plus{" "}
              <span className="num">{costData.methodology.hoursPer100Lines}</span> hour per 100
              changed lines, plus <span className="num">{costData.methodology.hoursPerExtraCommit}</span>{" "}
              hours per commit beyond the first. When the PR links to a JIRA issue with story points,
              the story-point estimate (<span className="num">{costData.methodology.hoursPerStoryPoint}</span>{" "}
              hours per point) is used if it is larger. Story points count once per issue even when
              several PRs link to the same ticket.
            </p>
            <p className={ed.small}>
              Estimates are then bounded so outliers cannot distort the report. A single PR counts
              for at most <span className="num">{costData.methodology.maxHoursPerPR}</span> hours and
              a single ticket at most <span className="num">{costData.methodology.maxHoursPerIssue}</span>{" "}
              hours. Diffs dominated by generated or vendored files (lockfiles, bundles, snapshots)
              are detected by their high lines-per-file ratio and contribute little beyond the base.
              Finally, no developer is credited more than a full-time schedule (
              <span className="num">{costData.methodology.hoursPerDay}</span> hours per active
              business day) over the window between their first and last merge in the period.
            </p>
            {anyBounds && adj && (
              <p className={ed.small}>
                In this period these bounds removed <span className="num">{fmtInt(adj.hoursRemovedByCaps)}</span>{" "}
                modeled hours of noise: <span className="num">{adj.prsCappedByPRLimit}</span> oversized{" "}
                {pluralWord(adj.prsCappedByPRLimit, "PR")} capped,{" "}
                <span className="num">{adj.prsDampenedAsGenerated}</span> generated-file{" "}
                {pluralWord(adj.prsDampenedAsGenerated, "diff")} dampened, and{" "}
                <span className="num">{adj.developersCappedByCapacity}</span>{" "}
                {pluralWord(adj.developersCappedByCapacity, "developer")} clamped to full-time capacity.
              </p>
            )}

            <h3 className={ed.h3Sm}>How work is classified</h3>
            <p className={ed.small}>
              Work is classified from the linked JIRA issue type when a PR is linked to a ticket,
              falling back to the PR&apos;s detected work category (from branch and title patterns),
              and finally to a conservative &quot;unlinked&quot; bucket that is always expensed.
            </p>
            <div className={tbl.scroll}>
              <table className={tbl.table}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Treatment</th>
                    <th>Tax credit (QRE)</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {costData.methodology.classificationRules.map((rule) => (
                    <tr key={rule.category}>
                      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{rule.label}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {rule.source === "jira"
                          ? "JIRA issue type"
                          : rule.source === "pr"
                            ? "PR work category"
                            : "Fallback"}
                      </td>
                      <td>
                        {rule.isCapitalizable ? (
                          <span className={tbl.label}>Capitalize</span>
                        ) : (
                          <span className={tbl.tag}>Expense</span>
                        )}
                      </td>
                      <td title={rule.qreRationale}>
                        {rule.qreEligibility === "likely" ? (
                          <span className={tbl.label}>Likely</span>
                        ) : (
                          <span className={tbl.tag}>
                            {rule.qreEligibility === "possible" ? "Possible" : "Excluded"}
                          </span>
                        )}
                      </td>
                      <td style={{ color: "var(--colors-ed-ink2)" }}>{rule.rationale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className={ed.h3Sm}>Caveats and data quality</h3>
            <ul className={ed.bullets}>
              {costData.methodology.caveats.map((caveat, i) => (
                <li key={i}>{caveat}</li>
              ))}
              <li>
                In this period, <span className="num">{costData.summary.linkageRate}%</span> of
                merged PRs (<span className="num">{fmtNum(costData.summary.linkedPRs)}</span> of{" "}
                <span className="num">{fmtNum(costData.summary.totalPRs)}</span>) are linked to a
                JIRA issue.
              </li>
            </ul>

            <h3 className={ed.h3Sm}>Accounting context</h3>
            <p className={ed.small}>{costData.methodology.standardsNote}</p>
          </>
        )}
      </div>
    </div>
  );
}

const FIGURE_ROWS = 24;

const styles = {
  taxIntro: css({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px 32px",
    mb: "12px",
  }),
  rateInput: css({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    whiteSpace: "nowrap",
  }),
  rateField: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: 0,
    px: "8px",
    py: "4px",
    bg: "ed.paper",
    fontFamily: "mono",
    fontSize: "13px",
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: "none",
    color: "ed.ink3",
    _focusWithin: { borderColor: "ed.ink" },
    "& input": {
      width: "64px",
      border: "none",
      outline: "none",
      fontFamily: "mono",
      fontSize: "13px",
      fontVariantNumeric: "tabular-nums",
      color: "ed.ink",
      bg: "transparent",
    },
  }),
};
